
import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { CustomerLayout } from '../../components/ui/Layout';
// Added missing ShieldCheck import from lucide-react
import { Check, Clock, ChefHat, Bike, ThumbsUp, ArrowLeft, Banknote, Sparkles, CreditCard, Smartphone, ShieldCheck } from 'lucide-react';
import { Order, OrderStatus } from '../../types';
import { supabase } from '../../services/supabaseClient';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';

const steps = [
  { status: 'PENDING', label: 'Enviado', icon: Clock },
  { status: 'CONFIRMED', label: 'Confirmado', icon: Check },
  { status: 'PREPARING', label: 'Preparando', icon: ChefHat },
  { status: 'READY', label: 'Saiu p/ Entrega', icon: Bike },
  { status: 'COMPLETED', label: 'Entregue', icon: ThumbsUp },
];

const OrderTrackingPage: React.FC = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const { addToast } = useToast();
  
  const [order, setOrder] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSuccessHeader, setShowSuccessHeader] = useState(false);

  const isStripeSuccess = searchParams.get('success') === 'true';

  useEffect(() => {
    if (isStripeSuccess) {
      clearCart();
      setShowSuccessHeader(true);
      addToast('Pagamento confirmado com sucesso!', 'success');
    }
  }, [isStripeSuccess, clearCart, addToast]);

  useEffect(() => {
    if (!orderId) return;

    const fetchOrder = async () => {
        try {
            const { data, error } = await supabase.from('orders').select('*').eq('id', orderId).single();
            if (error) throw error;
            if (data) {
                setOrder(data);
            }
        } catch (err) {
            setError("Pedido não encontrado.");
        } finally {
            setLoading(false);
        }
    };

    fetchOrder();

    const channel = supabase.channel(`order_track_${orderId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'orders', filter: `id=eq.${orderId}` }, (payload) => {
          setOrder((prev: any) => prev ? { ...prev, ...payload.new } : null);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [orderId]);

  const stepIndex = order ? steps.findIndex(s => s.status === order.status) : -1;

  if (loading) return <CustomerLayout showCart={false}><div className="flex justify-center items-center h-[60vh]"><div className="w-10 h-10 border-4 border-rose-500 border-t-transparent rounded-full animate-spin"></div></div></CustomerLayout>;

  if (error) return (
    <CustomerLayout showCart={false}>
      <div className="flex flex-col items-center justify-center h-[60vh] text-center px-6">
        <h2 className="text-4xl font-black uppercase italic tracking-tighter mb-4 text-gray-900">Ops!</h2>
        <p className="text-gray-500 font-medium mb-8">Não encontramos este pedido no nosso sistema.</p>
        <Link to="/" className="bg-gray-900 text-white px-8 py-4 rounded-full font-black text-xs uppercase tracking-widest">Voltar ao Início</Link>
      </div>
    </CustomerLayout>
  );

  const getPaymentIcon = () => {
    if (order.payment_method === 'STRIPE') return <CreditCard className="w-4 h-4 text-rose-500" />;
    if (order.payment_method === 'CASH') return <Banknote className="w-4 h-4 text-green-500" />;
    return <Smartphone className="w-4 h-4 text-blue-500" />;
  };

  const getPaymentLabel = () => {
    if (order.payment_method === 'STRIPE') return 'Pagamento Online';
    if (order.payment_method === 'CASH') return 'Dinheiro na Entrega';
    return 'Máquina na Entrega';
  };

  return (
    <CustomerLayout showCart={false}>
      <div className="max-w-2xl mx-auto px-6 pt-24 pb-32 animate-fade-in">
        <Link to={`/`} className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400 mb-12 hover:text-rose-500 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Voltar ao Início
        </Link>

        {showSuccessHeader && (
          <div className="bg-rose-500 text-white rounded-[2.5rem] p-10 mb-12 shadow-glow-rose relative overflow-hidden group">
              <Sparkles className="absolute right-[-20px] top-[-20px] w-40 h-40 opacity-20 group-hover:rotate-12 transition-transform" />
              <div className="relative z-10">
                <h2 className="text-3xl font-black italic uppercase tracking-tighter mb-2">PAGAMENTO APROVADO!</h2>
                <p className="text-rose-100 text-sm font-medium">Sua transação via Stripe foi processada com sucesso. Já estamos preparando seu pedido!</p>
              </div>
          </div>
        )}

        <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-black text-gray-900 tracking-tighter uppercase italic leading-none mb-4">
              {order?.status === 'CANCELLED' ? 'PEDIDO CANCELADO' : 'STATUS DO <br/> SEU PEDIDO.'}
            </h1>
            <div className="inline-flex items-center gap-3 px-6 py-2 bg-gray-100 rounded-full border border-gray-200">
              <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Protocolo: #{order?.short_id}</span>
            </div>
        </div>

        {/* Barra de Progresso Kanban Style */}
        <div className="bg-white rounded-[3.5rem] shadow-premium p-10 md:p-16 mb-12 relative overflow-hidden border border-gray-100">
            <div className="absolute left-[3.75rem] top-16 bottom-16 w-1 bg-gray-50">
                <div className="w-full bg-rose-500 transition-all duration-1000 ease-in-out" style={{ height: `${(Math.max(0, stepIndex) / (steps.length - 1)) * 100}%` }} />
            </div>

            <div className="space-y-12 relative">
                {steps.map((step, idx) => {
                    const isActive = idx <= stepIndex;
                    const isCurrent = idx === stepIndex;
                    const Icon = step.icon;
                    return (
                        <div key={step.status} className="flex items-center gap-10">
                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center z-10 transition-all duration-700 ${isActive ? 'bg-rose-500 text-white shadow-glow-rose scale-110' : 'bg-gray-50 text-gray-200 border border-gray-100'}`}>
                                <Icon className="w-6 h-6" />
                            </div>
                            <div className={`${isActive ? 'opacity-100' : 'opacity-20'} transition-opacity`}>
                                <h3 className={`font-black text-xs uppercase tracking-[0.2em] ${isCurrent ? 'text-rose-600' : 'text-gray-900'}`}>{step.label}</h3>
                                {isCurrent && <p className="text-[10px] text-rose-400 font-black uppercase tracking-widest animate-pulse mt-1">EM ANDAMENTO...</p>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>

        {/* Resumo Financeiro e Detalhes de Pagamento */}
        <div className="bg-gray-900 text-white rounded-[3rem] p-10 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/5 blur-3xl" />
            
            <div className="flex justify-between items-start mb-10 pb-6 border-b border-white/5">
              <div>
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">Método de Pagamento</p>
                <div className="flex items-center gap-3">
                   {getPaymentIcon()}
                   <span className="text-sm font-black uppercase italic">{getPaymentLabel()}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">Status Pagto</p>
                <span className={`text-[10px] font-black uppercase px-3 py-1 rounded-full ${order?.payment_status === 'paid' ? 'bg-green-500/20 text-green-500' : 'bg-amber-500/20 text-amber-500'}`}>
                  {order?.payment_status === 'paid' ? 'CONCLUÍDO' : 'PENDENTE'}
                </span>
              </div>
            </div>

            {/* Informação de Troco se for Dinheiro */}
            {order.payment_method === 'CASH' && order.payment_details?.changeFor && (
              <div className="mb-10 bg-white/5 p-6 rounded-2xl border border-white/10 flex justify-between items-center">
                 <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Levar troco para:</span>
                 <span className="text-xl font-black text-green-500 italic">R$ {order.payment_details.changeFor}</span>
              </div>
            )}

            <h3 className="text-[9px] font-black uppercase tracking-[0.4em] text-gray-600 mb-8">Itens do Pedido</h3>
            <div className="space-y-4 mb-10">
                {order?.items.map((item: any, i: number) => (
                    <div key={i} className="flex justify-between text-sm">
                        <span className="text-gray-400 font-medium">
                          <span className="text-rose-500 font-black mr-2">{item.quantity}x</span> 
                          {item.productName}
                        </span>
                        <span className="font-black italic">R$ {item.totalPrice.toFixed(2)}</span>
                    </div>
                ))}
            </div>
            
            <div className="pt-8 border-t border-white/10 flex justify-between items-end">
                <div>
                  <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">Total Geral</p>
                  <p className="text-5xl font-black text-rose-500 italic tracking-tighter">R$ {order?.total.toFixed(2).replace('.', ',')}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-2">Segurança Fluxil</p>
                  <ShieldCheck className="w-8 h-8 text-white/20 inline-block" />
                </div>
            </div>
        </div>

        <p className="text-center mt-12 text-[9px] font-black text-gray-400 uppercase tracking-[0.5em] italic">
          Fluxil Cloud Infrastructure <span className="text-rose-500">.</span>
        </p>
      </div>
    </CustomerLayout>
  );
};

export default OrderTrackingPage;
